/**
 * File: assets/basari-product-tabs.js
 * Purpose: Architecture foundation for Basari PDP Product Detail Tabs.
 * Sprint: Architecture Review — lifecycle events + shared reviews count API.
 * Owner: Basari Theme / PDP
 *
 * In scope:
 *   - Activate / deactivate tabs + panels
 *   - aria-selected / tabindex / hidden updates
 *   - ArrowLeft / ArrowRight / Home / End keyboard support on tablist
 *   - Lifecycle events (before / change / after)
 *   - Shared reviews count store sync (BasariReviewsCount)
 *
 * Out of scope (future sprints):
 *   - Reviews business logic / AJAX / provider APIs
 *   - Visual animations beyond class toggles
 *
 * Events (bubble from [data-basari-pdp-tabs]):
 *   basari:pdp-tabs:before-change  { tab, previousTab, sectionId, cancelable }
 *   basari:pdp-tabs:change         { tab, previousTab, sectionId }
 *   basari:pdp-tabs:after-change   { tab, previousTab, sectionId }
 *
 * Reviews (document / root):
 *   basari:reviews:provider-mounted { provider, productId, sectionId, root }
 *   basari:reviews:count-change     { count, previousCount, productId, sectionId }
 *
 * Count API (single source of truth — providers call this, not templates):
 *   window.BasariReviewsCount.get(root?)
 *   window.BasariReviewsCount.set(count, { root?, provider? })
 *   window.BasariReviewsCount.sync(root?)
 *   window.BasariReviewsCount.notifyProviderMounted(root, { provider })
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-basari-pdp-tabs]';
  var TAB_SELECTOR = '[data-basari-pdp-tabs-tab]';
  var PANEL_SELECTOR = '[data-basari-pdp-tabs-panel]';
  var COUNT_STORE_SELECTOR = '[data-basari-reviews-count-store]';
  var ACTIVE_CLASS = 'is-active';

  function emit(target, name, detail, cancelable) {
    var event = new CustomEvent(name, {
      bubbles: true,
      cancelable: !!cancelable,
      detail: detail || {},
    });
    (target || document).dispatchEvent(event);
    return event;
  }

  function parseStore(root) {
    var scope = root || document;
    var node = scope.querySelector
      ? scope.querySelector(COUNT_STORE_SELECTOR)
      : document.querySelector(COUNT_STORE_SELECTOR);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function writeStore(root, data) {
    var scope = root || document;
    var node = scope.querySelector
      ? scope.querySelector(COUNT_STORE_SELECTOR)
      : document.querySelector(COUNT_STORE_SELECTOR);
    if (!node) return;
    node.textContent = JSON.stringify(data);
  }

  function resolveRoot(root) {
    if (root && root.getAttribute && root.getAttribute('data-basari-pdp-tabs') !== null) {
      return root;
    }
    if (root && root.closest) {
      var closest = root.closest(ROOT_SELECTOR);
      if (closest) return closest;
    }
    return document.querySelector(ROOT_SELECTOR);
  }

  /**
   * Shared reviews count — one write updates store, section root, nav, and reviews root.
   */
  var BasariReviewsCount = {
    get: function (root) {
      var scope = resolveRoot(root) || document;
      var data = parseStore(scope);
      if (data && typeof data.count === 'number') return data.count;
      var attr = scope.getAttribute && scope.getAttribute('data-basari-reviews-count');
      return attr ? parseInt(attr, 10) || 0 : 0;
    },

    set: function (count, options) {
      var opts = options || {};
      var scope = resolveRoot(opts.root) || document.querySelector(ROOT_SELECTOR);
      if (!scope) return 0;

      var next = parseInt(count, 10);
      if (isNaN(next) || next < 0) next = 0;

      var previous = BasariReviewsCount.get(scope);
      var data = parseStore(scope) || {};
      data.count = next;
      if (opts.provider) data.provider = opts.provider;
      writeStore(scope, data);

      BasariReviewsCount.sync(scope);

      if (previous !== next) {
        emit(scope, 'basari:reviews:count-change', {
          count: next,
          previousCount: previous,
          productId: data.productId || scope.getAttribute('data-product-id'),
          sectionId: data.sectionId || scope.getAttribute('data-section-id'),
          provider: data.provider || 'placeholder',
        });
      }

      return next;
    },

    sync: function (root) {
      var scope = resolveRoot(root) || document.querySelector(ROOT_SELECTOR);
      if (!scope) return;

      var data = parseStore(scope) || {};
      var count = typeof data.count === 'number' ? data.count : BasariReviewsCount.get(scope);
      var countStr = String(count);

      scope.setAttribute('data-basari-reviews-count', countStr);

      scope.querySelectorAll('[data-basari-reviews-count]').forEach(function (el) {
        el.setAttribute('data-basari-reviews-count', countStr);
      });

      scope.querySelectorAll('[data-basari-pdp-tabs-reviews-count]').forEach(function (el) {
        el.textContent = '(' + countStr + ')';
      });
    },

    /**
     * Call after a review provider mounts into [data-basari-reviews-mount].
     * Architecture hook only — no provider logic here.
     */
    notifyProviderMounted: function (root, meta) {
      var scope = resolveRoot(root) || document.querySelector(ROOT_SELECTOR);
      if (!scope) return;

      var info = meta || {};
      var data = parseStore(scope) || {};
      if (info.provider) {
        data.provider = info.provider;
        writeStore(scope, data);
        var reviewsRoot = scope.querySelector('[data-basari-reviews-root]');
        if (reviewsRoot) {
          reviewsRoot.setAttribute('data-basari-reviews-provider', info.provider);
        }
      }

      emit(scope, 'basari:reviews:provider-mounted', {
        provider: (info.provider || data.provider || 'placeholder'),
        productId: data.productId || scope.getAttribute('data-product-id'),
        sectionId: data.sectionId || scope.getAttribute('data-section-id'),
        root: scope,
      });
    },
  };

  /**
   * @param {HTMLElement} root
   */
  function BasariPdpTabs(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.tablist = root.querySelector('[data-basari-pdp-tabs-nav]');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll(TAB_SELECTOR));
    this.panels = Array.prototype.slice.call(root.querySelectorAll(PANEL_SELECTOR));
    this.activeTab = root.getAttribute('data-default-tab') || '';
    this._onTabClick = this._onTabClick.bind(this);
    this._onTabKeydown = this._onTabKeydown.bind(this);
  }

  BasariPdpTabs.prototype.init = function () {
    if (!this.tabs.length) return;

    BasariReviewsCount.sync(this.root);

    var defaultTab = this.root.getAttribute('data-default-tab');
    var hasDefault = defaultTab && this._findTab(defaultTab);

    this.tabs.forEach(
      function (tab) {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      }.bind(this)
    );

    if (hasDefault) {
      this.activate(defaultTab, { focus: false, silent: true });
    }
  };

  /**
   * @param {string} tabId
   * @returns {HTMLElement|null}
   */
  BasariPdpTabs.prototype._findTab = function (tabId) {
    for (var i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].getAttribute('data-basari-pdp-tabs-tab') === tabId) {
        return this.tabs[i];
      }
    }
    return null;
  };

  /**
   * @param {string} tabId
   * @returns {HTMLElement|null}
   */
  BasariPdpTabs.prototype._findPanel = function (tabId) {
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i].getAttribute('data-basari-pdp-tabs-panel') === tabId) {
        return this.panels[i];
      }
    }
    return null;
  };

  /**
   * Activate a tab by id. Architecture foundation — no AJAX / reviews logic.
   * @param {string} tabId
   * @param {{ focus?: boolean, silent?: boolean }} [options]
   */
  BasariPdpTabs.prototype.activate = function (tabId, options) {
    var opts = options || {};
    var nextTab = this._findTab(tabId);
    var nextPanel = this._findPanel(tabId);
    if (!nextTab || !nextPanel) return;

    var previousTab = this.activeTab || '';

    if (!opts.silent) {
      var before = emit(
        this.root,
        'basari:pdp-tabs:before-change',
        {
          tab: tabId,
          previousTab: previousTab,
          sectionId: this.sectionId,
        },
        true
      );
      if (before.defaultPrevented) return;
    }

    this.tabs.forEach(function (tab) {
      var isActive = tab === nextTab;
      tab.classList.toggle(ACTIVE_CLASS, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach(function (panel) {
      var isActive = panel === nextPanel;
      panel.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.activeTab = tabId;

    if (opts.focus) {
      nextTab.focus();
    }

    if (!opts.silent) {
      var detail = {
        tab: tabId,
        previousTab: previousTab,
        sectionId: this.sectionId,
      };
      emit(this.root, 'basari:pdp-tabs:change', detail);
      emit(this.root, 'basari:pdp-tabs:after-change', detail);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  BasariPdpTabs.prototype._onTabClick = function (event) {
    var tab = event.currentTarget;
    var tabId = tab.getAttribute('data-basari-pdp-tabs-tab');
    if (!tabId) return;
    this.activate(tabId, { focus: false });
  };

  /**
   * Keyboard navigation per WAI-ARIA Tabs pattern (horizontal).
   * @param {KeyboardEvent} event
   */
  BasariPdpTabs.prototype._onTabKeydown = function (event) {
    var key = event.key;
    var currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    var nextIndex = null;

    switch (key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    var nextTab = this.tabs[nextIndex];
    var tabId = nextTab.getAttribute('data-basari-pdp-tabs-tab');
    this.activate(tabId, { focus: true });
  };

  BasariPdpTabs.prototype.destroy = function () {
    this.tabs.forEach(
      function (tab) {
        tab.removeEventListener('click', this._onTabClick);
        tab.removeEventListener('keydown', this._onTabKeydown);
      }.bind(this)
    );
  };

  /**
   * @param {ParentNode} [scope]
   */
  function initAll(scope) {
    var rootNode = scope && scope.querySelectorAll ? scope : document;
    var roots = rootNode.querySelectorAll(ROOT_SELECTOR);
    roots.forEach(function (root) {
      if (root.__basariPdpTabs) return;
      var instance = new BasariPdpTabs(root);
      instance.init();
      root.__basariPdpTabs = instance;
    });
  }

  function onSectionLoad(event) {
    if (!event || !event.target) return;
    initAll(event.target);
  }

  function onSectionUnload(event) {
    if (!event || !event.target) return;
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (root && root.__basariPdpTabs) {
      root.__basariPdpTabs.destroy();
      delete root.__basariPdpTabs;
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);

  window.BasariPdpTabs = BasariPdpTabs;
  window.BasariReviewsCount = BasariReviewsCount;
})();
